import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Copy, Video, Users, Calendar, Clock, Check, Edit2, Link2, Lock, Power, PowerOff } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = window.location.origin;
const getAuthHeader = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const MeetingTab = () => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [copied, setCopied] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "", scheduled_date: "", scheduled_time: "", password: ""
  });

  const fetchMeetings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/meetings/admin/list`, { headers: getAuthHeader() });
      setMeetings(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchMeetings(); }, [fetchMeetings]);

  const resetForm = () => {
    setForm({ title: "", description: "", scheduled_date: "", scheduled_time: "", password: "" });
    setEditId(null);
    setShowForm(false);
  };

  const generatePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let pw = "";
    for (let i = 0; i < 6; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    setForm({ ...form, password: pw });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await axios.put(`${API}/meetings/admin/${editId}`, form, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API}/meetings/admin/create`, form, { headers: getAuthHeader() });
      }
      resetForm();
      fetchMeetings();
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const deleteMeeting = async (id) => {
    if (!window.confirm("Delete this meeting?")) return;
    await axios.delete(`${API}/meetings/admin/${id}`, { headers: getAuthHeader() });
    fetchMeetings();
  };

  const toggleActive = async (m) => {
    await axios.put(`${API}/meetings/admin/${m.id}`, { is_active: !m.is_active }, { headers: getAuthHeader() });
    fetchMeetings();
  };

  const buildInvite = (m) => {
    const link = `${SITE_URL}/meeting/${m.id}`;
    let msg = `📹 You're invited to a meeting!\n\n`;
    msg += `Meeting: ${m.title}\n`;
    if (m.scheduled_date) {
      const d = new Date(m.scheduled_date + (m.scheduled_time ? `T${m.scheduled_time}` : ""));
      msg += `Date: ${d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}`;
      if (m.scheduled_time) msg += ` at ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
      msg += "\n";
    }
    if (m.description) msg += `Details: ${m.description}\n`;
    msg += `\nJoin Link: ${link}\nPassword: ${m.password}\n`;
    msg += `\nPowered by ASN Meeting Space`;
    return msg;
  };

  const copyInvite = (m) => {
    navigator.clipboard.writeText(buildInvite(m));
    setCopied(m.id);
    setTimeout(() => setCopied(null), 2000);
  };

  const copyLink = (m) => {
    navigator.clipboard.writeText(`${SITE_URL}/meeting/${m.id}`);
    setCopied(`link-${m.id}`);
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-white/20 border-t-orange-500 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="meeting-admin-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Video className="w-5 h-5 text-orange-500" />
          <h2 className="text-white font-bold text-lg uppercase tracking-tight">Meeting Space</h2>
          <span className="text-zinc-500 text-xs">{meetings.length} meeting{meetings.length !== 1 ? "s" : ""}</span>
        </div>
        <button onClick={() => { resetForm(); generatePassword(); setShowForm(!showForm); }}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-black font-bold text-sm uppercase" data-testid="meeting-create-btn">
          <Plus className="w-4 h-4" /> New Meeting
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-zinc-900 border border-white/10 p-6 space-y-4" data-testid="meeting-form">
          <h3 className="text-white font-bold text-sm uppercase tracking-wider">{editId ? "Edit Meeting" : "Schedule New Meeting"}</h3>
          <div>
            <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1">Title *</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required placeholder="Weekly Team Standup" className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" data-testid="meeting-title-input" />
          </div>
          <div>
            <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Meeting agenda or details..." className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm resize-none" data-testid="meeting-desc-input" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1"><Calendar className="w-3 h-3 inline mr-1" />Date</label>
              <input type="date" value={form.scheduled_date} onChange={e => setForm({ ...form, scheduled_date: e.target.value })} className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" data-testid="meeting-date-input" />
            </div>
            <div>
              <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1"><Clock className="w-3 h-3 inline mr-1" />Time</label>
              <input type="time" value={form.scheduled_time} onChange={e => setForm({ ...form, scheduled_time: e.target.value })} className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" data-testid="meeting-time-input" />
            </div>
            <div>
              <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1"><Lock className="w-3 h-3 inline mr-1" />Password *</label>
              <div className="flex gap-2">
                <input value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required placeholder="Meeting password" className="flex-1 bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm font-mono" data-testid="meeting-password-input" />
                <button type="button" onClick={generatePassword} className="px-3 py-2 bg-zinc-700 text-zinc-300 text-xs uppercase hover:bg-zinc-600">Generate</button>
              </div>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-black font-bold text-sm uppercase" data-testid="meeting-save-btn">{editId ? "Update" : "Create"}</button>
            <button type="button" onClick={resetForm} className="px-6 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm uppercase">Cancel</button>
          </div>
        </form>
      )}

      <div className="space-y-3" data-testid="meeting-list">
        {meetings.length === 0 ? (
          <div className="text-center py-16 bg-zinc-900/50 border border-white/5">
            <Video className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">No meetings yet. Create your first one!</p>
          </div>
        ) : meetings.map(m => (
          <div key={m.id} className={`border ${m.is_active ? "border-green-500/20 bg-zinc-900/50" : "border-white/5 bg-zinc-900/30 opacity-60"}`} data-testid={`meeting-${m.id}`}>
            <div className="flex items-start justify-between gap-4 px-5 py-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="text-white font-bold text-sm">{m.title}</h3>
                  {m.is_active ? (
                    <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-green-500/20 text-green-400">Active</span>
                  ) : (
                    <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-red-500/20 text-red-400">Ended</span>
                  )}
                  <span className="text-zinc-600 text-xs flex items-center gap-1"><Users className="w-3 h-3" />{m.participant_count}/6</span>
                </div>
                {m.description && <p className="text-zinc-500 text-xs mb-1">{m.description}</p>}
                <div className="flex items-center gap-4 text-zinc-600 text-xs flex-wrap">
                  {m.scheduled_date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(m.scheduled_date + (m.scheduled_time ? `T${m.scheduled_time}` : "")).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                      {m.scheduled_time && ` at ${new Date(`2000-01-01T${m.scheduled_time}`).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`}
                    </span>
                  )}
                  <span className="flex items-center gap-1 font-mono"><Lock className="w-3 h-3" />{m.password}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => copyInvite(m)} title="Copy invite message" className={`p-2 transition-colors ${copied === m.id ? "text-green-400" : "text-zinc-500 hover:text-white"}`} data-testid={`meeting-copy-invite-${m.id}`}>
                  {copied === m.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
                <button onClick={() => copyLink(m)} title="Copy join link" className={`p-2 transition-colors ${copied === `link-${m.id}` ? "text-green-400" : "text-zinc-500 hover:text-white"}`} data-testid={`meeting-copy-link-${m.id}`}>
                  {copied === `link-${m.id}` ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                </button>
                <button onClick={() => toggleActive(m)} title={m.is_active ? "End meeting" : "Reactivate"} className="p-2 text-zinc-500 hover:text-white" data-testid={`meeting-toggle-${m.id}`}>
                  {m.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                </button>
                <button onClick={() => { setForm({ title: m.title, description: m.description || "", scheduled_date: m.scheduled_date || "", scheduled_time: m.scheduled_time || "", password: m.password }); setEditId(m.id); setShowForm(true); }}
                  className="p-2 text-zinc-500 hover:text-white" data-testid={`meeting-edit-${m.id}`}>
                  <Edit2 className="w-4 h-4" />
                </button>
                <button onClick={() => deleteMeeting(m.id)} className="p-2 text-zinc-500 hover:text-red-400" data-testid={`meeting-delete-${m.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
                <a href={`/meeting/${m.id}`} target="_blank" rel="noreferrer" className="p-2 text-orange-500 hover:text-orange-400" title="Join meeting" data-testid={`meeting-join-${m.id}`}>
                  <Video className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
